import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { categories, menuItems } from "./data";

export const MenuSection = () => {
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? menuItems : menuItems.filter((item) => item.category === active);

  return (
    <section id="menu" className="relative px-5 py-24 sm:px-8 sm:py-32" data-testid="menu-section">
      <div className="mx-auto max-w-7xl">
        <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow mb-5">03 · From Our Kitchen</p>
            <h2 className="font-display text-3xl leading-[1.2] tracking-tight text-cream sm:text-4xl">
              Made fresh,
              <br />
              <span className="gold-text italic">served royal.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-ash">
            A taste of the menu — real plates from our kitchen, priced easy and served warm, 8 AM to 11 PM.
          </p>
        </Reveal>

        <Reveal className="mb-10 flex flex-wrap gap-2.5" role="tablist" aria-label="Menu categories">
          {categories.map((category) => (
            <button
              key={category}
              role="tab"
              aria-selected={active === category}
              onClick={() => setActive(category)}
              data-testid={`menu-filter-${category.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`rounded-full border px-5 py-2 font-mono text-[0.65rem] uppercase tracking-[0.18em] transition-colors duration-300 ${
                active === category
                  ? "border-gold bg-gold text-onyx"
                  : "border-cream/15 text-ash hover:border-gold/50 hover:text-gold-light"
              }`}
            >
              {category}
            </button>
          ))}
        </Reveal>

        <motion.div layout className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((item, index) => (
              <motion.article
                layout
                key={item.name}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="gold-ring-card group overflow-hidden rounded-2xl bg-coal"
                data-testid={`menu-card-${index}`}
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 rounded-full border border-gold/30 bg-onyx/70 px-3 py-1 font-mono text-[0.58rem] uppercase tracking-[0.22em] text-gold-light backdrop-blur-md">
                    {item.category}
                  </span>
                </div>
                <div className="flex items-start justify-between gap-4 p-5">
                  <div>
                    <h3 className="font-display text-lg text-cream">{item.name}</h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-ash">{item.description}</p>
                  </div>
                  <strong className="whitespace-nowrap font-mono text-base font-medium text-gold-light">{item.price}</strong>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
